<script setup>
import { computed, useId } from 'vue';
import { useI18n } from 'vue-i18n';
import { PhxIcon, PhxButtonAnimatedRevealMask } from '@phx-styleguides/jlc';
import { useWatchComparator } from '@maison/composables/watchComparator';

const props = defineProps({
  reference: { type: String, required: true },
  modelReference: { type: String, default: '' },
  name: { type: String, default: '' },
  image: { type: String, default: '' },
  link: { type: String, default: '' },
  variant: {
    type: String,
    default: 'icon',
    validator: (value) => ['icon', 'checkbox', 'reveal'].includes(value),
  },
  buttonClasses: {
    type: Array,
    default: () => ['btn', 'btn--default', 'btn--xs', 'btn--icon-only', 'btn--icon', 'btn--transparent'],
  },
  iconSize: { type: String, default: '16' },
});

const { t } = useI18n();
const { isInWatchComparator, toggle } = useWatchComparator();

const inputId = useId();
const isActive = computed(() => isInWatchComparator(props.reference));
const icon = computed(() => `compare-${props.iconSize}`);
const ariaLabel = computed(() =>
  t(isActive.value ? 'WatchComparator.button.remove.ariaLabel' : 'WatchComparator.button.add.ariaLabel'),
);
const label = computed(() => t('WatchComparator.compare.label'));
const revealLabel = computed(() =>
  t(isActive.value ? 'WatchComparator.remove.label' : 'WatchComparator.compare.label'),
);

const onToggle = (event) => {
  try {
    const added = toggle({
      reference: props.reference,
      modelReference: props.modelReference,
      name: props.name,
      image: props.image,
      link: props.link,
    });

    if (!added) {
      if (event?.target instanceof HTMLInputElement) {
        event.target.checked = false;
      }

      window.Phoenix.toaster.notify({
        type: 'info',
        text: t('WatchComparator.error.full'),
      });
    }
  } catch {
    window.Phoenix.toaster.notify({
      type: 'error',
      text: t('WatchComparator.error.general'),
    });
  }
};
</script>

<template>
  <div v-if="variant === 'checkbox'" class="checkbox product-card__checkbox">
    <input
      :id="inputId"
      type="checkbox"
      :name="name"
      :checked="isActive"
      class="checkbox__input"
      data-cy="watch-comparator-button"
      @click.stop
      @change="onToggle"
    />
    <label :for="inputId" class="checkbox__label">{{ label }}</label>
  </div>

  <phx-button-animated-reveal-mask
    v-else-if="variant === 'reveal'"
    icon-caption="end"
    :text="revealLabel"
    :icon-id="icon"
    :icon-size="iconSize"
    :class="[...buttonClasses, { 'is-active': isActive }]"
    :aria-label="ariaLabel"
    :aria-pressed="isActive"
    data-cy="watch-comparator-button"
    @click.prevent.stop="onToggle"
  />

  <button
    v-else
    type="button"
    :class="[...buttonClasses, { 'is-active': isActive }]"
    :aria-label="ariaLabel"
    :aria-pressed="isActive"
    data-cy="watch-comparator-button"
    @click.prevent.stop="onToggle"
  >
    <span class="btn__icon">
      <phx-icon :icon="icon" :size="iconSize" />
    </span>
  </button>
</template>
